/**
 * "Bolter del día" — proyector de la ready room (Fase 5g, extra).
 *
 * Proyecta en la pantalla del proyector de `/portaaviones.html` al
 * piloto con la peor puntuación de la última misión, siempre que esa
 * puntuación quede por debajo de un `(OK)` (3 pts). Es para picar a los
 * pilotos — de ahí el nombre.
 *
 * Si varios pilotos empatan en la peor puntuación, van rotando: el
 * proyector se "apaga y enciende" entre uno y otro. Con un solo bolter,
 * tarjeta fija. Si nadie quedó por debajo de OK, estado neutro.
 *
 * Datos: `GET /api/greenie-board` (las mismas medias del board) +
 * `GET /api/pilots` (foto de perfil). Sin endpoints nuevos. El reparto
 * de efectos de proyección (bloom, parpadeo) lo pone `ready-room.css`.
 */

import type { GalleryPhoto, GreenieBoardPilot } from '@51esc/shared';
import { fetchGallery } from '../galeria/api.js';
import { fetchGreenieBoard } from '../greenie-board/api.js';
import { fetchPilots } from '../logbook/api.js';

/**
 * Umbral del Bolter del día: la puntuación de un `(OK)` (3 pts). Si la
 * media de la última misión de un piloto queda por debajo de esto, entra
 * como Bolter del día. (Mismo valor que `LSO_GRADE_POINTS['(OK)']` en el
 * parser; no se importa porque vive en el paquete `api`.)
 */
const BOLTER_THRESHOLD = 3;

/** Tiempo que cada bolter está en pantalla antes de rotar al siguiente. */
const DWELL_MS = 6000;
/** Duración del apagado del proyector entre un bolter y el siguiente. */
const POWER_OFF_MS = 360;
/**
 * Intervalo del slideshow de la galería cuando no hay Bolter del día.
 * El usuario pidió 1 s: una sucesión rápida de imágenes que da vida al
 * proyector sin convertirse en distracción de la sala.
 */
const GALLERY_INTERVAL_MS = 1000;

/** Un piloto candidato a Bolter del día. */
interface BolterCandidate {
  callsign: string;
  /** Media de puntos LSO del piloto en la última misión (2 decimales). */
  score: number;
}

/** Resultado del cálculo: la misión y los pilotos empatados en lo peor. */
interface BolterOfDay {
  missionName: string;
  candidates: BolterCandidate[];
}

/**
 * Arranca el "Bolter del día": localiza el host del proyector, carga los
 * datos y pinta. Si no hay host (otra página) no hace nada.
 */
export function startBolterOfDay(): void {
  const host = document.querySelector<HTMLElement>('[data-zone="bolter"]');
  if (host) void load(host);
}

async function load(host: HTMLElement): Promise<void> {
  const [gb, pilots] = await Promise.all([fetchGreenieBoard(), fetchPilots()]);
  // Sin datos del board no proyectamos nada — la pantalla del fondo
  // queda tal cual; no tiene sentido un proyector encendido sin contenido.
  if (!gb.ok) return;

  const avatars = new Map<string, string>();
  if (pilots.ok) {
    for (const p of pilots.data) {
      if (p.avatarUrl) avatars.set(p.callsign.toLowerCase(), p.avatarUrl);
    }
  }

  host.innerHTML =
    '<div class="rr-projector-screen">' +
    '<div class="rr-projector-content"></div>' +
    '</div>';
  const screen = host.querySelector<HTMLElement>('.rr-projector-screen');
  const content = host.querySelector<HTMLElement>('.rr-projector-content');
  if (!screen || !content) return;

  const bolter = computeBolterOfDay(gb.data.pilots);
  if (!bolter) {
    // Sin Bolter del día → el proyector no se queda en negro: arranca
    // un slideshow de la galería del 51 que da vida a la sala. Si la
    // galería está vacía / no se puede cargar, cae al mensaje neutro.
    await startGalleryFallback(content);
    return;
  }

  const cards = bolter.candidates.map((c) => renderCard(c, bolter.missionName, avatars));
  content.innerHTML = cards[0] ?? '';

  // Un solo bolter → tarjeta fija. Varios empatados → rotación con
  // apagado/encendido del proyector.
  if (cards.length > 1) startCycle(screen, content, cards);
}

/**
 * Slideshow de la galería para el proyector cuando no hay Bolter del
 * día. Carga `/api/screenshots` y, si hay al menos una foto, las muestra
 * en bucle a `GALLERY_INTERVAL_MS` cambiando el `src` del mismo `<img>`.
 * Con una sola foto o `prefers-reduced-motion`, se queda fija sin
 * iterar. Si la galería está vacía o el fetch falla, cae al mensaje
 * neutro original.
 */
async function startGalleryFallback(content: HTMLElement): Promise<void> {
  const result = await fetchGallery();
  const photos: GalleryPhoto[] =
    result.ok && result.data.photos.length > 0 ? result.data.photos : [];
  if (photos.length === 0) {
    content.innerHTML = renderNeutral();
    return;
  }

  // Orden aleatorio fijo en el arranque para que cada sesión vea las
  // fotos en distinto orden sin volver a aleatorizar en cada ciclo
  // (eso provocaría repeticiones cercanas).
  const shuffled = [...photos].sort(() => Math.random() - 0.5);

  content.innerHTML =
    `<img class="rr-gallery-photo" src="${esc(shuffled[0]!.url)}" alt="" />`;
  const imgEl = content.querySelector<HTMLImageElement>('.rr-gallery-photo');
  if (!imgEl) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (shuffled.length === 1 || reduced) return;

  // Pre-cachear las siguientes 5 imágenes para que el primer cambio no
  // muestre un flash de imagen rota. A partir de ahí el browser ya las
  // tiene en cache.
  for (const p of shuffled.slice(1, 6)) {
    const pre = new Image();
    pre.src = p.url;
  }

  let idx = 0;
  window.setInterval(() => {
    idx = (idx + 1) % shuffled.length;
    const next = shuffled[idx];
    if (next) imgEl.src = next.url;
  }, GALLERY_INTERVAL_MS);
}

/** Rota entre las tarjetas con el efecto de apagado/encendido. */
function startCycle(
  screen: HTMLElement,
  content: HTMLElement,
  cards: string[],
): void {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let idx = 0;

  window.setInterval(() => {
    const next = (idx + 1) % cards.length;
    idx = next;
    if (reduced) {
      // Sin animación: cambio directo de tarjeta.
      content.innerHTML = cards[next] ?? '';
      return;
    }
    screen.classList.add('is-off');
    window.setTimeout(() => {
      content.innerHTML = cards[next] ?? '';
      screen.classList.remove('is-off');
      // Breve calentamiento de lámpara al encender.
      screen.classList.add('is-warming');
      window.setTimeout(() => screen.classList.remove('is-warming'), 700);
    }, POWER_OFF_MS);
  }, DWELL_MS);
}

// ─── Cálculo ───────────────────────────────────────────────────────────

/**
 * Determina el Bolter del día: el peor piloto de la última misión, si su
 * media queda por debajo de OK. `null` si no hay misiones o si hasta el
 * peor apontó seguro.
 */
function computeBolterOfDay(pilots: GreenieBoardPilot[]): BolterOfDay | null {
  // 1. Última misión = la del `dateIso` más reciente.
  let lastMissionId: string | null = null;
  let lastMissionName = '';
  let lastDate = '';
  for (const p of pilots) {
    for (const pass of p.passes) {
      if (pass.dateIso > lastDate) {
        lastDate = pass.dateIso;
        lastMissionId = pass.missionReportId;
        lastMissionName = pass.missionName;
      }
    }
  }
  if (lastMissionId === null) return null;

  // 2. Puntuación de cada piloto en esa misión = media de sus pases.
  const round2 = (n: number): number => Math.round(n * 100) / 100;
  const scored: BolterCandidate[] = [];
  for (const p of pilots) {
    const pts: number[] = [];
    for (const pass of p.passes) {
      if (
        pass.missionReportId === lastMissionId &&
        pass.averagePoints !== null
      ) {
        pts.push(pass.averagePoints);
      }
    }
    if (pts.length === 0) continue;
    const score = pts.reduce((s, n) => s + n, 0) / pts.length;
    scored.push({ callsign: p.callsign, score: round2(score) });
  }
  if (scored.length === 0) return null;

  // 3. Peor puntuación; gate: tiene que estar por debajo de `(OK)`.
  let worst = Infinity;
  for (const s of scored) if (s.score < worst) worst = s.score;
  if (worst >= BOLTER_THRESHOLD) return null;

  // 4. Todos los pilotos empatados en esa peor puntuación.
  const candidates = scored.filter((s) => s.score === worst);
  return { missionName: lastMissionName, candidates };
}

// ─── Render ────────────────────────────────────────────────────────────

function renderCard(
  c: BolterCandidate,
  missionName: string,
  avatars: Map<string, string>,
): string {
  const url = avatars.get(c.callsign.toLowerCase());
  const photo = url
    ? `<img class="rr-bolter-photo" src="${esc(url)}" alt="" />`
    : `<div class="rr-bolter-photo rr-bolter-photo--empty" aria-hidden="true">` +
      `${esc(c.callsign.slice(0, 1).toUpperCase())}</div>`;
  return `
    <div class="rr-bolter">
      <div class="rr-bolter-title">BOLTER DEL DÍA</div>
      <div class="rr-bolter-main">
        ${photo}
        <div class="rr-bolter-info">
          <span class="rr-bolter-name">${esc(c.callsign)}</span>
          <span class="rr-bolter-mission">${esc(missionName)}</span>
          <span class="rr-bolter-score">${c.score.toFixed(2)} pts de media</span>
        </div>
      </div>
    </div>
  `;
}

function renderNeutral(): string {
  return `
    <div class="rr-bolter rr-bolter--none">
      <div class="rr-bolter-title">BOLTER DEL DÍA</div>
      <p class="rr-bolter-none-msg">
        Sin bolter del día.<br />Todos apontaron seguros.
      </p>
    </div>
  `;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
